import PropTypes from 'prop-types';
import React, { ReactNode } from 'react';

import { IComponent } from '../../../typings/generic-component';
import { IClassNames, makeClassNames, applyColorSchema } from '../../../utils/styles';
import Cta from '../../atoms/cta/cta';
import IconMotorDashboard from '../../atoms/icons/icon-motor-dashboard';
import Text from '../../atoms/text/text';
import { ColorSchema, colorSchemaList } from '../../../typings/design-tokens';

import './courtesy-message.scss';

export interface ICourtesyMessage extends IComponent {
  cta?: {
    href?: string;
    text?: string;
  };
  text?: string;
  date?: string;
  backgroundIcon?: ReactNode;
  cardLayout?: boolean;
  colorSchema?: ColorSchema;
}

export const defaultBackgroundIcon = () => <IconMotorDashboard size="xxlarge" color="medium-light" />;

const classNames = (
  { colorSchema }: IClassNames,
  cardLayout: boolean,
  cta?: { href?: string; text?: string }
): IClassNames =>
  makeClassNames({
    CourtesyMessage: [
      'courtesy-message',
      cta && 'courtesy-message--withCta',
      `${applyColorSchema('courtesy-message', colorSchema)}`,
      cardLayout && 'courtesy-message--card-layout',
    ],
    CourtesyMessageIcon: 'courtesy-message--icon',
    CourtesyMessageText: 'courtesy-message--text',
    CourtesyMessageTextDate: 'courtesy-message--text-date',
    CourtesyMessageCta: 'courtesy-message--cta',
  });

const CourtesyMessage: React.FC<ICourtesyMessage> = ({
  text,
  date,
  cta,
  backgroundIcon = defaultBackgroundIcon(),
  fontSize = '22',
  colorSchema = 'light',
  cardLayout = false,
}) => {
  const cn = classNames({ colorSchema }, cardLayout, cta);

  return (
    <div className={cn.CourtesyMessage}>
      {backgroundIcon && <div className={cn.CourtesyMessageIcon}>{backgroundIcon}</div>}
      {cta && (
        <div className={cn.CourtesyMessageCta}>
          <Cta href={cta.href} iconPosition="right" mobileButton>
            {cta.text}
          </Cta>
        </div>
      )}
      <div className={cn.CourtesyMessageText}>
        {text && (
          <Text color={colorSchema === 'dark' ? 'light' : 'dark'} fontSize={fontSize} fontWeight="400">
            {`${text} `}
          </Text>
        )}
        {date && (
          <Text
            className={cn.CourtesyMessageTextDate}
            color={colorSchema === 'dark' ? 'light' : 'dark'}
            fontSize={fontSize}
            fontWeight="500"
          >
            {date}
          </Text>
        )}
      </div>
    </div>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  CourtesyMessage.propTypes = {
    backgroundIcon: PropTypes.node,
    colorSchema: PropTypes.oneOf(colorSchemaList),
    cta: PropTypes.shape({
      href: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
    }),
    date: PropTypes.string,
    text: PropTypes.string,
  };
}

export default CourtesyMessage;
